import type { FeedResponse, Listing, Stats, Brand, StatePatch } from './types';
import { ApiErr } from './types';
import { sessionExpired } from './stores/session';

const BASE = '/api';

/**
 * `raw401` opts out of the central "401 ⇒ session expired" rewrite. The login
 * endpoint returns 401 `bad_credentials` for a wrong password (see
 * internal/api/handlers_auth.go); that must reach the caller as its own envelope,
 * not be masked as "phiên hết hạn" or flip the session store.
 */
async function req<T>(path: string, init?: RequestInit, opts?: { raw401?: boolean }): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      ...init,
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch (e) {
    throw new ApiErr(0, 'network', `không kết nối được máy chủ: ${(e as Error).message}`);
  }

  if (res.status === 401 && !opts?.raw401) {
    sessionExpired();
    throw new ApiErr(401, 'unauthenticated', 'phiên hết hạn');
  }

  const noBody = res.status === 204 || res.headers.get('content-length') === '0';
  const body: unknown = noBody ? null : await res.json().catch(() => null);

  if (!res.ok) {
    const env = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiErr(res.status, env?.code ?? 'unknown', env?.message ?? `HTTP ${res.status}`);
  }
  return body as T;
}

export const api = {
  login: (password: string) =>
    req<{ ok: true }>('/login', { method: 'POST', body: JSON.stringify({ password }) }, { raw401: true }),
  logout: () => req<{ ok: true }>('/logout', { method: 'POST' }),
  feed: (query = '') => req<FeedResponse>('/feed' + (query ? `?${query}` : '')),
  listing: (id: number) => req<Listing>(`/listing/${id}`),
  setState: (id: number, patch: StatePatch) =>
    req<{ ok: true }>(`/listing/${id}/state`, { method: 'POST', body: JSON.stringify(patch) }),
  stats: () => req<Stats>('/stats'),
  brands: () => req<{ brands: Brand[] }>('/brands'),
};
